import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { groupsAPI } from '../../services/api';
import './GroupsAndClubs.css';
import '../../styles/shared/PageHeader.css';
import GroupCard from '../../components/features/Groups/GroupCard';
import AddGroupForm from '../../components/features/Groups/AddGroupForm';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import { errorHandler } from '../../utils/errorHandler';
import { formatGroupType } from '../../utils/formatters';

const GroupsAndClubs = () => {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [error, setError] = useState(null);
    const [selectedType, setSelectedType] = useState('all');
    const [retryCount, setRetryCount] = useState(0);
    const { currentUser, hasRole } = useAuth();
    const isEditor = hasRole('editor');
    const isAdmin = hasRole('admin');
    const isSuperAdmin = hasRole('superAdmin');
    const [pagination, setPagination] = useState({
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 1
    });

    useEffect(() => {
        fetchGroups();
    }, []);

    const fetchGroups = async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch from API with retry
            const response = await groupsAPI.getAllGroups();

            if (!response || !response.groups) {
                throw new Error('No groups data received');
            }

            // Ensure we have an array of groups
            const groupsArray = Array.isArray(response.groups) ? response.groups : [];
            
            setGroups(groupsArray);

            // Update pagination state if available
            if (response.pagination) {
                setPagination({
                    total: response.total || 0,
                    page: response.page || 1,
                    limit: response.limit || 10,
                    totalPages: response.totalPages || 1
                });
            }
        } catch (error) {
            setError(errorHandler.processError(error).message);
            setRetryCount(prev => prev + 1);
        } finally {
            setLoading(false);
        }
    };

    // Retry fetching if we have an error
    useEffect(() => {
        if (error && retryCount < 3) {
            const timer = setTimeout(() => {
                fetchGroups();
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [error, retryCount]);

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return (
            <div className="error-message">
                <p>{error}</p>
                {retryCount < 3 && <p>Retrying in 5 seconds...</p>}
                <button 
                    onClick={() => {
                        setRetryCount(0);
                        fetchGroups();
                    }}
                    className="retry-button"
                >
                    Retry Now
                </button>
            </div>
        );
    }

    const handleDeleteGroup = async (groupId) => {
        if (!isSuperAdmin) return;
        
        if (window.confirm('Are you sure you want to delete this group? This action cannot be undone.')) {
            try {
                await groupsAPI.deleteGroup(groupId);
                await fetchGroups();
            } catch (err) {
                setError(errorHandler.processError(err).message);
            }
        }
    };

    const filteredGroups = selectedType === 'all' 
        ? groups 
        : groups.filter(group => group.type === selectedType);

    return (
        <div className="groups-and-clubs-page">
            <div className="page-header">
                <div className="page-header-content">
                    <h1>Groups & Clubs</h1>
                    <p className="page-header-description">
                        Discover our diverse network of clubs, councils, project teams, and city teams 
                        working together to make a difference.
                    </p>
                    {isAdmin && (
                        <div className="page-header-buttons">
                            <button 
                                className="page-header-button"
                                onClick={() => setShowAddForm(true)}
                            >
                                <i className="fas fa-plus"></i> Add New Group
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="groups-filters">
                <button 
                    className={selectedType === 'all' ? 'active' : ''} 
                    onClick={() => setSelectedType('all')}
                >
                    All
                </button>
                <button 
                    className={selectedType === 'club' ? 'active' : ''} 
                    onClick={() => setSelectedType('club')}
                >
                    {formatGroupType('club')}
                </button>
                <button 
                    className={selectedType === 'localCouncil' ? 'active' : ''} 
                    onClick={() => setSelectedType('localCouncil')}
                >
                    {formatGroupType('localCouncil')}
                </button>
                <button 
                    className={selectedType === 'projectTeam' ? 'active' : ''} 
                    onClick={() => setSelectedType('projectTeam')}
                >
                    {formatGroupType('projectTeam')}
                </button>
                <button 
                    className={selectedType === 'cityTeam' ? 'active' : ''} 
                    onClick={() => setSelectedType('cityTeam')}
                >
                    {formatGroupType('cityTeam')}
                </button>
            </div>

            {showAddForm && (
                <AddGroupForm 
                    onClose={() => setShowAddForm(false)}
                    onGroupAdded={() => {
                        fetchGroups();
                        setShowAddForm(false);
                    }}
                />
            )}

            <div className="groups-grid">
                {filteredGroups.length === 0 ? (
                    <p className="no-groups">
                        {selectedType === 'all' 
                            ? 'No groups have been added yet.'
                            : `No ${selectedType}s found.`}
                    </p>
                ) : (
                    filteredGroups.map(group => (
                        <GroupCard 
                            key={group.id}
                            group={group}
                            isAdmin={isAdmin}
                            isEditor={isEditor}
                            isSuperAdmin={isSuperAdmin}
                            onUpdate={fetchGroups}
                            onDelete={handleDeleteGroup}
                        />
                    ))
                )}
            </div>

            {pagination.totalPages > 1 && (
                <div className="pagination">
                    <button 
                        onClick={() => fetchGroups(pagination.page - 1)}
                        disabled={pagination.page === 1}
                        className="pagination-button"
                    >
                        Previous
                    </button>
                    <span className="pagination-info">
                        Page {pagination.page} of {pagination.totalPages}
                    </span>
                    <button 
                        onClick={() => fetchGroups(pagination.page + 1)}
                        disabled={pagination.page === pagination.totalPages}
                        className="pagination-button"
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

export default GroupsAndClubs; 